import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import RoleBasedHeader from '../../components/ui/RoleBasedHeader';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import AchievementConfiguration from './components/AchievementConfiguration';
import TierManagement from './components/TierManagement';
import PointsEconomyControls from './components/PointsEconomyControls';
import RewardsCatalog from './components/RewardsCatalog';
import EngagementAnalytics from './components/EngagementAnalytics';
import ChallengeCreation from './components/ChallengeCreation';
import ReferralProgramConfig from './components/ReferralProgramConfig';

const AchievementRewardsManagement = () => {
  const { userProfile } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [overviewStats, setOverviewStats] = useState(null);

  useEffect(() => {
    fetchOverviewStats();
  }, []);

  const fetchOverviewStats = async () => {
    setLoading(true);
    try {
      // Mock data - replace with actual Supabase queries
      const mockStats = {
        totalMembers: 1247,
        activeParticipants: 892,
        participationRate: 71.5,
        totalPointsIssued: 1245000,
        totalRewardsRedeemed: 342,
        averageEngagementScore: 78,
        retentionImprovement: 12.3,
        lifetimeValueIncrease: 18.7
      };
      setOverviewStats(mockStats);
    } catch (error) {
      console.error('Error fetching overview stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'LayoutDashboard' },
    { id: 'achievements', label: 'Achievements', icon: 'Award' },
    { id: 'tiers', label: 'Tier Management', icon: 'TrendingUp' },
    { id: 'points', label: 'Points Economy', icon: 'Coins' },
    { id: 'rewards', label: 'Rewards Catalog', icon: 'Gift' },
    { id: 'analytics', label: 'Analytics', icon: 'BarChart3' },
    { id: 'challenges', label: 'Challenges', icon: 'Target' },
    { id: 'referrals', label: 'Referral Program', icon: 'Users' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <RoleBasedHeader />
        <div className="page-content-wrapper">
          <div className="flex items-center justify-center h-[calc(100vh-64px)]">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-muted-foreground">Loading gamification management...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <RoleBasedHeader />
      <div className="page-content-wrapper">
        <div className="container mx-auto px-4 py-8 max-w-7xl">
          {/* Header Section */}
          <div className="mb-8">
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-2">
              Achievement & Rewards Management
            </h1>
            <p className="text-muted-foreground">
              Configure gamification elements and track member engagement
            </p>
          </div>

          {/* Tab Navigation */}
          <div className="mb-6 border-b border-border overflow-x-auto">
            <div className="flex gap-2 min-w-max">
              {tabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors ${
                    activeTab === tab?.id
                      ? 'border-primary text-primary font-medium' :'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Icon name={tab?.icon} size={18} />
                  <span>{tab?.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div className="space-y-6">
            {activeTab === 'overview' && (
              <>
                {/* Key Metrics */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div className="card">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <p className="text-sm text-muted-foreground mb-1">Participation Rate</p>
                        <h3 className="text-3xl font-heading font-bold text-foreground">
                          {overviewStats?.participationRate}%
                        </h3>
                      </div>
                      <div className="w-12 h-12 rounded-lg bg-primary/20 flex items-center justify-center">
                        <Icon name="Users" size={24} color="var(--color-primary)" />
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {overviewStats?.activeParticipants} of {overviewStats?.totalMembers} members
                    </p>
                  </div>

                  <div className="card">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <p className="text-sm text-muted-foreground mb-1">Points Issued</p>
                        <h3 className="text-3xl font-heading font-bold text-foreground">
                          {(overviewStats?.totalPointsIssued / 1000)?.toFixed(0)}K
                        </h3>
                      </div>
                      <div className="w-12 h-12 rounded-lg bg-accent/20 flex items-center justify-center">
                        <Icon name="Coins" size={24} color="var(--color-accent)" />
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Total points distributed
                    </p>
                  </div>

                  <div className="card">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <p className="text-sm text-muted-foreground mb-1">Rewards Redeemed</p>
                        <h3 className="text-3xl font-heading font-bold text-foreground">
                          {overviewStats?.totalRewardsRedeemed}
                        </h3>
                      </div>
                      <div className="w-12 h-12 rounded-lg bg-success/20 flex items-center justify-center">
                        <Icon name="Gift" size={24} color="var(--color-success)" />
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      This month
                    </p>
                  </div>

                  <div className="card">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <p className="text-sm text-muted-foreground mb-1">Engagement Score</p>
                        <h3 className="text-3xl font-heading font-bold text-foreground">
                          {overviewStats?.averageEngagementScore}
                        </h3>
                      </div>
                      <div className="w-12 h-12 rounded-lg bg-warning/20 flex items-center justify-center">
                        <Icon name="TrendingUp" size={24} color="var(--color-warning)" />
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Average member score
                    </p>
                  </div>
                </div>

                {/* ROI Analysis */}
                <div className="card bg-gradient-to-r from-primary/10 via-accent/10 to-success/10 border-2 border-primary/20">
                  <h2 className="text-xl font-heading font-semibold text-foreground mb-4">
                    Gamification ROI Impact
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-lg bg-success/20 flex items-center justify-center flex-shrink-0">
                        <Icon name="Heart" size={24} color="var(--color-success)" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-heading font-bold text-foreground mb-1">
                          +{overviewStats?.retentionImprovement}%
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          Improvement in member retention rate since gamification launch
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-lg bg-primary/20 flex items-center justify-center flex-shrink-0">
                        <Icon name="DollarSign" size={24} color="var(--color-primary)" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-heading font-bold text-foreground mb-1">
                          +{overviewStats?.lifetimeValueIncrease}%
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          Increase in member lifetime value through enhanced engagement
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Button
                    variant="outline"
                    fullWidth
                    iconName="Award"
                    onClick={() => setActiveTab('achievements')}
                  >
                    Create Achievement
                  </Button>
                  <Button
                    variant="outline"
                    fullWidth
                    iconName="Target"
                    onClick={() => setActiveTab('challenges')}
                  >
                    Launch Challenge
                  </Button>
                  <Button
                    variant="outline"
                    fullWidth
                    iconName="Gift"
                    onClick={() => setActiveTab('rewards')}
                  >
                    Add Reward
                  </Button>
                </div>
              </>
            )}

            {activeTab === 'achievements' && <AchievementConfiguration />}
            {activeTab === 'tiers' && <TierManagement />}
            {activeTab === 'points' && <PointsEconomyControls />}
            {activeTab === 'rewards' && <RewardsCatalog />}
            {activeTab === 'analytics' && <EngagementAnalytics />}
            {activeTab === 'challenges' && <ChallengeCreation />}
            {activeTab === 'referrals' && <ReferralProgramConfig />}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AchievementRewardsManagement;